import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import DeleteInfoModal from './DeleteInfoModal';
import { ListVariantSource } from '../../../_shared/types/auction';
import { FilterNames, TradeStateName } from '../../../_shared/types/trade';
import FilterService from '../../../services/FilterService';
import ListService from '../../../services/ListService';
import MixpanelService from '../../../services/MixpanelService';
import { localStorageService } from '../../../services/StorageService';
import TradeListService from '../../../services/TradeListService';
import Alert from '../../../_shared/components/Alert';
import ConfirmModal from '../../../_shared/components/ConfirmModal';
import FilterDisplayBox from '../../../_shared/components/FilterDisplayBox';
import PopoverWrapper from '../../../_shared/components/Popover';
import Toggle from '../../../_shared/components/Toggle';
import { CacheKeys } from '../../../_shared/constants/filters';
import { httpCodes } from '../../../_shared/constants/httpMethods';
import MixpanelConfig from '../../../_shared/constants/mixpanelConfig';
import { FILTER_SETTINGS } from '../../../_shared/constants/userSettings';

import {
  StyledSavedFilters,
  StyledSelectedFilter,
  StyledDisplayFilterName,
  StyledNotificationsToggle,
  Title,
  ArrowDownIcon,
  Dropdown,
  DropdownHeader,
  FilterOptions,
  FilterName,
  TrashCan
} from './Styles';
import TRToast from '../../../_shared/helpers/TRToast';

const propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      value: PropTypes.number,
      label: PropTypes.string
    })
  ),
  defaultValue: PropTypes.shape({
    type: PropTypes.number,
    id: PropTypes.string,
    title: PropTypes.string
  }),
  onChange: PropTypes.func,
  tradeState: PropTypes.number.isRequired
};

const defaultProps = {
  options: [],
  defaultValue: {},
  onChange: () => {}
};

const SavedFilters = ({
  options = [],
  defaultValue,
  onChange,
  isEditing,
  refetchFilters,
  tradeState,
  setEnableNotification,
  settings
}) => {
  const tradeListService = TradeListService.get();
  const listService = ListService.get();
  const mixpanel = MixpanelService.get();
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [delFilter, setDelFilter] = useState(null);
  const [filterCriteria, listVariant] = useSelector(
    state => [
      state?.filter?.filterCriteria?.criteria,
      state?.ui?.layout?.listVariant
    ],
    shallowEqual
  );
  const [bypassToggle, setBypassToggle] = useState(false);

  useEffect(() => {
    // Do not allow toggle if the user account has notifications OFF
    setBypassToggle(
      settings?.userNotificationSettings?.presetId === FILTER_SETTINGS.off
    );
  }, [settings]);

  const confirmModalLogic = useMemo(() => {
    const toggleOriginalState = selectedFilter?.enableNotification;
    return [
      {
        title: (
          <FormattedMessage id="list.filters.save.notificationsOff.title" />
        ),
        msg: <FormattedMessage id="list.filters.save.notificationsOff.msg" />,
        confirmLabel: <FormattedMessage id="modal.ok" />,
        onConfirm: () => () => {
          setIsConfirmModalOpen(false);
          setSelectedFilter(filter => ({
            ...filter,
            enableNotification: toggleOriginalState
          }));
        }
      }
    ];
  }, [selectedFilter]);

  const changeFilter = useCallback(
    filter => {
      setSelectedFilter(filter);
      onChange(filter);
    },
    [onChange]
  );

  const closeDeleteModal = async (shouldDelete, filter) => {
    if (!shouldDelete) {
      setIsDeleteModalOpen(false);
      return;
    }

    const index = options.findIndex(
      _filters => _filters.id === FilterNames.all.id
    );
    const delIndex = options.findIndex(_filters => _filters.id === filter.id);

    setSelectedFilter(options[index]);
    onChange(options[index]);
    options.splice(delIndex, 1);

    listService.deleteList(filter.id);
    setIsDeleteModalOpen(false);

    // Refetch the saved filters list and remove selected filter from cache if previously selected
    await refetchFilters();
    const cachedFilter = localStorageService.getItem(CacheKeys.selectedFilter);
    const statesWithDeletedFilter = Object.keys(cachedFilter).filter(
      state => cachedFilter[state] === filter.id
    );

    if (statesWithDeletedFilter?.length) {
      const newCachedFilter = {};
      statesWithDeletedFilter.forEach(state => {
        newCachedFilter[state] = FilterNames.all.id;
      });

      localStorageService.setItem(CacheKeys.selectedFilter, {
        ...cachedFilter,
        ...newCachedFilter
      });
    }
  };

  const onDeleteFilter = useCallback((event, filter) => {
    event.stopPropagation();

    setDelFilter(filter);
    setIsDeleteModalOpen(true);
  }, []);

  const renderChild = useCallback(() => {
    return openPopOver => (
      <FilterDisplayBox
        openDropDown={() => {
          openPopOver(true);
        }}
        title={
          !FilterService.isDGNFilter(selectedFilter?.type)
            ? selectedFilter?.title
            : ''
        }
        id={selectedFilter?.id}
        testId="filter-dropdown"
      >
        <ArrowDownIcon />
      </FilterDisplayBox>
    );
  }, [selectedFilter]);

  const canDelete = filter => {
    return (
      filter.id !== FilterNames.all.id &&
      filter.id !== FilterNames.watchlist.id &&
      filter.id !== FilterNames.impound.id &&
      filter.id !== FilterNames.independent.id &&
      filter.id !== FilterNames.franchise.id
    );
  };

  const renderContent = useCallback(() => {
    return openPopOver => (
      <Dropdown>
        <DropdownHeader>
          {/* Select */}
          <FormattedMessage id="tr5.select" />
        </DropdownHeader>
        {options.map(option => {
          return (
            <FilterOptions
              noBorder={
                option.type === FilterNames.default.type ||
                option.type === FilterNames.custom.type
              }
              isSelected={selectedFilter.id === option.id}
              key={option.id}
              onClick={() => {
                openPopOver(false);
                changeFilter(option);
              }}
              // TODO: figure out better identifiers for saved filters
              data-testid={`filter-option-${option.id}`}
            >
              <FilterName>{option.title}</FilterName>
              {canDelete(option) && (
                <TrashCan
                  clickHandler={e => onDeleteFilter(e, option)}
                  data-testid={`delete-filter-option-${option.id}`}
                />
              )}
            </FilterOptions>
          );
        })}
      </Dropdown>
    );
  }, [changeFilter, onDeleteFilter, options, selectedFilter?.id]);

  const handleToggle = async () => {
    const notificationOriginalState = selectedFilter.enableNotification;
    // If user has notifications completely off, show warning modal and do not send API call if they are trying to toggle tohe notification on
    if (
      settings?.userNotificationSettings?.presetId === FILTER_SETTINGS.off &&
      !selectedFilter.enableNotification
    ) {
      setIsConfirmModalOpen(true);
      return;
    }

    const {
      res,
      enableNotification
    } = await tradeListService.toggleNotifications(
      selectedFilter,
      filterCriteria
    );

    // Update state of enableNotification for tracking in ConfirmModalContainer
    setEnableNotification(!notificationOriginalState);
    setSelectedFilter(filter => ({
      ...filter,
      enableNotification: !notificationOriginalState
    }));

    refetchFilters();

    if (res?.status === httpCodes.OK) {
      // If API call was successful and user was toggling notifications ON, show toaster
      if (enableNotification) {
        TRToast(
          <Alert
            variant="success"
            messages={[
              <FormattedMessage id="list.filters.notification.success" />
            ]}
          />
        );
      }
      mixpanel.track(MixpanelConfig.names.filterNotification, {
        toggle_on: enableNotification,
        trade_state: TradeStateName[tradeState],
        list_type: ListVariantSource[listVariant]
      });
    } else {
      // Revert enableNotification states if call fails
      setTimeout(() => {
        setEnableNotification(notificationOriginalState);
        setSelectedFilter(filter => ({
          ...filter,
          enableNotification: notificationOriginalState
        }));
      }, 300);
      TRToast(
        <Alert
          variant="error"
          messages={[<FormattedMessage id="list.filters.notification.error" />]}
        />
      );
    }
  };

  useEffect(() => {
    if (defaultValue) {
      setSelectedFilter(defaultValue);
    }
  }, [defaultValue]);

  useEffect(() => {
    if (selectedFilter?.id !== FilterNames.all.id) {
      setEnableNotification(selectedFilter?.enableNotification);
    }
  });

  return (
    <StyledSavedFilters
      isFilterSelected={
        selectedFilter && selectedFilter.id !== FilterNames.all.id
      }
      data-testid="savedFilters"
    >
      <Title>
        <FormattedMessage id="list.filters.mysavedfilters" />
      </Title>
      <PopoverWrapper
        align="start"
        position={['bottom']}
        renderContent={renderContent()}
        renderChild={renderChild()}
      />
      {selectedFilter &&
        selectedFilter.id !== FilterNames.all.id &&
        !FilterService.isDGNFilter(selectedFilter.type) && (
          <StyledSelectedFilter>
            <StyledDisplayFilterName
              isWatchlistOrImpound={
                selectedFilter.id === FilterNames.watchlist.id ||
                selectedFilter.id === FilterNames.impound.id
              }
            >
              {isEditing && (
                <span>
                  <FormattedMessage id="list.filters.isEditing" />
                </span>
              )}{' '}
              {selectedFilter.title}
            </StyledDisplayFilterName>
            {!(
              selectedFilter.id === FilterNames.watchlist.id ||
              selectedFilter.id === FilterNames.impound.id
            ) && (
              <StyledNotificationsToggle>
                <FormattedMessage id="list.filters.notification.label" />
                <Toggle
                  testId="toggle-filter-notification"
                  onClick={handleToggle}
                  isChecked={selectedFilter.enableNotification}
                  byPassUpdate={bypassToggle}
                />
              </StyledNotificationsToggle>
            )}
          </StyledSelectedFilter>
        )}
      <DeleteInfoModal
        isOpen={isDeleteModalOpen}
        closeModal={closeDeleteModal}
        filter={delFilter}
      />
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        modalLogic={confirmModalLogic}
        loadingMsg={<FormattedMessage id="modal.confirmation.loading" />}
      />
    </StyledSavedFilters>
  );
};

SavedFilters.propTypes = propTypes;
SavedFilters.defaultProps = defaultProps;

export default SavedFilters;
